import { useMemo } from 'react';
import { CONTRACT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { useRenewalAction } from '../renewal/useRenewalAction';
import { useChangeContractAction } from '../change/useChangeContractAction';
import { ContractActions } from '../types';
import { useCancelTerminationAction } from '../cancel-termination/useCancelTerminationAction';
import { useTerminateAction } from '../terminate/useTerminateAction';
import { useUpdatePaymentMethodAction } from '../update-payment-method/useUpdatePaymentMethodAction';
import { useManageTerminationAction } from '../manage-termination/useManageTerminationAction';
import { useDeleteContractAction } from '../delete/useDeleteContractAction';
import { hasContractM1Gate, hasM2ChangesWithoutProrationGate } from '../../../utils/gates';
import { useContractData } from '../../../hooks/useFetchContractData';
import { ContractStatus, ObjectSource } from '../../../constants';
import { useFetchAllowedActions } from './useAllowedActions';
export function useContractActions({
  objectId,
  objectTypeId,
  skipAll
}) {
  var _query$data3, _query$data4;
  const skip = skipAll || !objectId || objectId === -1 || objectTypeId !== CONTRACT_TYPE_ID;
  const query = useContractData({
    objectId,
    objectTypeId
  }, {
    skip
  });
  // TODO: update this to use the allowed actions RPC once it's implemented
  useFetchAllowedActions({
    objectId,
    objectTypeId
  }, {
    skip
  });
  // const allowedActions = useAllowedActions(allowedActionsSelector);

  const terminateAction = useTerminateAction({
    objectId,
    objectTypeId,
    skip
  });
  const changeAction = useChangeContractAction({
    objectId,
    objectTypeId,
    skip
  });
  const renewalAction = useRenewalAction({
    objectId,
    objectTypeId,
    skip
  });
  const cancelTerminationAction = useCancelTerminationAction({
    objectId,
    objectTypeId,
    skip
  });
  const updatePaymentMethodAction = useUpdatePaymentMethodAction({
    objectId,
    objectTypeId,
    skip
  });
  const manageTerminationAction = useManageTerminationAction({
    objectId,
    objectTypeId,
    skip
  });
  const deleteAction = useDeleteContractAction({
    objectId,
    objectTypeId,
    skip
  });
  return useMemo(() => {
    var _query$data, _query$data2;
    const objectSource = (_query$data = query.data) === null || _query$data === void 0 || (_query$data = _query$data.properties) === null || _query$data === void 0 || (_query$data = _query$data.hs_object_source) === null || _query$data === void 0 ? void 0 : _query$data.value;
    const contractTerminated = ((_query$data2 = query.data) === null || _query$data2 === void 0 || (_query$data2 = _query$data2.properties) === null || _query$data2 === void 0 || (_query$data2 = _query$data2.hs_status) === null || _query$data2 === void 0 ? void 0 : _query$data2.value) === ContractStatus.Terminated;
    if (!hasContractM1Gate() || contractTerminated) {
      return {};
    }
    if (objectSource === ObjectSource.CRM_UI) {
      // default actions for a contract record (dumb CRM objects)
      return {
        [ContractActions.DELETE_CONTRACT]: deleteAction
      };
    }
    if (!hasM2ChangesWithoutProrationGate()) {
      return {
        [ContractActions.UPDATE_PAYMENT_METHOD]: updatePaymentMethodAction,
        [ContractActions.RESCHEDULE_TERMINATION]: manageTerminationAction,
        [ContractActions.CANCEL_TERMINATION]: cancelTerminationAction,
        [ContractActions.TERMINATE]: terminateAction
      };
    }
    return {
      [ContractActions.UPDATE_PAYMENT_METHOD]: updatePaymentMethodAction,
      [ContractActions.CREATE_CHANGE_QUOTE]: changeAction,
      [ContractActions.CREATE_RENEWAL_QUOTE]: renewalAction,
      [ContractActions.RESCHEDULE_TERMINATION]: manageTerminationAction,
      [ContractActions.CANCEL_TERMINATION]: cancelTerminationAction,
      [ContractActions.TERMINATE]: terminateAction
    }; // satisfies Record<ContractActions, Action>;
  }, [(_query$data3 = query.data) === null || _query$data3 === void 0 || (_query$data3 = _query$data3.properties) === null || _query$data3 === void 0 || (_query$data3 = _query$data3.hs_object_source) === null || _query$data3 === void 0 ? void 0 : _query$data3.value, (_query$data4 = query.data) === null || _query$data4 === void 0 || (_query$data4 = _query$data4.properties) === null || _query$data4 === void 0 || (_query$data4 = _query$data4.hs_status) === null || _query$data4 === void 0 ? void 0 : _query$data4.value, updatePaymentMethodAction, changeAction, renewalAction, manageTerminationAction, cancelTerminationAction, terminateAction, deleteAction]);
}
export function useContractActionsList({
  objectId,
  objectTypeId
}) {
  var _data$properties2;
  const {
    data
  } = useContractData({
    objectId,
    objectTypeId
  });
  const actions = useContractActions({
    objectId,
    objectTypeId
  });
  // const allowedActions = useAllowedActions(allowedActionsSelector);
  return useMemo(() => {
    var _data$properties, _actions$UPDATE_PAYME, _actions$CREATE_CHANG, _actions$CREATE_RENEW, _actions$RESCHEDULE_T, _actions$CANCEL_TERMI, _actions$TERMINATE;
    if ((data === null || data === void 0 || (_data$properties = data.properties) === null || _data$properties === void 0 || (_data$properties = _data$properties.hs_object_source) === null || _data$properties === void 0 ? void 0 : _data$properties.value) === ObjectSource.CRM_UI) {
      var _actions$DELETE_CONTR;
      return [...((_actions$DELETE_CONTR = actions.DELETE_CONTRACT) !== null && _actions$DELETE_CONTR !== void 0 && _actions$DELETE_CONTR.isAvailable() ? [actions.DELETE_CONTRACT] : [])];
    }
    const actionsList = [...((_actions$UPDATE_PAYME = actions.UPDATE_PAYMENT_METHOD) !== null && _actions$UPDATE_PAYME !== void 0 && _actions$UPDATE_PAYME.isAvailable() ? [actions.UPDATE_PAYMENT_METHOD] : []), ...((_actions$CREATE_CHANG = actions.CREATE_CHANGE_QUOTE) !== null && _actions$CREATE_CHANG !== void 0 && _actions$CREATE_CHANG.isAvailable() ? [actions.CREATE_CHANGE_QUOTE] : []), ...((_actions$CREATE_RENEW = actions.CREATE_RENEWAL_QUOTE) !== null && _actions$CREATE_RENEW !== void 0 && _actions$CREATE_RENEW.isAvailable() ? [actions.CREATE_RENEWAL_QUOTE] : [])];
    if (actionsList.length > 0) {
      actionsList.push('divider');
    }
    return [...actionsList, ...((_actions$RESCHEDULE_T = actions.RESCHEDULE_TERMINATION) !== null && _actions$RESCHEDULE_T !== void 0 && _actions$RESCHEDULE_T.isAvailable() ? [actions.RESCHEDULE_TERMINATION] : []), ...((_actions$CANCEL_TERMI = actions.CANCEL_TERMINATION) !== null && _actions$CANCEL_TERMI !== void 0 && _actions$CANCEL_TERMI.isAvailable() ? [actions.CANCEL_TERMINATION] : []), ...((_actions$TERMINATE = actions.TERMINATE) !== null && _actions$TERMINATE !== void 0 && _actions$TERMINATE.isAvailable() ? [actions.TERMINATE] : [])];
  }, [actions.CANCEL_TERMINATION, actions.CREATE_CHANGE_QUOTE, actions.CREATE_RENEWAL_QUOTE, actions.DELETE_CONTRACT, actions.RESCHEDULE_TERMINATION, actions.TERMINATE, actions.UPDATE_PAYMENT_METHOD, data === null || data === void 0 || (_data$properties2 = data.properties) === null || _data$properties2 === void 0 || (_data$properties2 = _data$properties2.hs_object_source) === null || _data$properties2 === void 0 ? void 0 : _data$properties2.value]);
}